// The project page of a usage project: a header card like the real project
// view's, then a strip with one step per analysis (the stages), and for the
// selected stage two tabs: its report, and its moves (the action items). A
// stage's items all end, done or canceled, before the next stage exists.

import { FC, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import {
    Alert,
    DataTableActionButton,
    Dropdown,
    Empty,
    Result,
    Segmented,
    Tooltip,
    message,
} from "../../../../design-system";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { useUserState } from "../../../../common/UserContext";
import { Field, HeaderCard, OwnerName } from "../components/HeaderCard";
import { SavingsProgress } from "../../../negotiations/v2/list/components/DealSavings";
import {
    contractValue,
    dealById,
    effectiveProjects,
    stageComplete,
    vendorOfDeal,
} from "../model/derived";
import { daysUntil, longDate, money, monthShort, relativeDays, shortDate } from "../model/format";
import { personName } from "../model/fixtures";
import { recordDecision, requestPlaybook, setItemStatus, useUsageDemoState } from "../model/store";
import { PLATFORM_ACTION_LABEL } from "../model/types";
import type {
    ActionItem,
    AnalysisKind,
    PlatformAction,
    UsageAnalysisRevision,
    UsageProject,
} from "../model/types";
import { usageDealPath } from "../paths";
import { reportHtml } from "../reports";
import { ActionItemStatus, UsageMeasureCell, UsageStageCell } from "../components/cells";
import classes from "./UsageProjectView.module.less";

/** The stages a usage project goes through, in order; the last one comes after signing. */
type StageKind = AnalysisKind | "savings";

const STAGE_ORDER: StageKind[] = ["snapshot", "usage", "playbook", "savings"];

const STAGE_TITLE: Record<StageKind, string> = {
    snapshot: "Opportunity snapshot",
    usage: "Usage analysis",
    playbook: "Negotiation playbook",
    savings: "Savings report",
};

/** One step of the strip: a stage, the analyses it has so far and its items. */
interface Stage {
    kind: StageKind;
    revisions: UsageAnalysisRevision[];
    items: ActionItem[];
    /** Reached: at least one analysis exists, so it can be opened. */
    reached: boolean;
}

const stagesOf = (project: UsageProject): Stage[] =>
    STAGE_ORDER.map((kind) => {
        const revisions = project.analyses
            .filter((a) => a.kind === kind)
            .sort((a, b) => a.revision - b.revision);
        return {
            kind,
            revisions,
            items: project.actionItems
                .filter((item) => item.stage === kind)
                .sort((a, b) => a.due.localeCompare(b.due)),
            reached: revisions.length > 0,
        };
    });

const isOverdue = (item: ActionItem) => item.status === "open" && daysUntil(item.due) < 0;

/** The other ways out of an item, behind the split button's chevron; "done" is the plain button's own. */
type ItemOption = "snooze" | "auto_renew" | "done" | "reopen";

const OPTION_LABEL: Record<ItemOption, string> = {
    snooze: "Snooze",
    auto_renew: "Let it auto-renew",
    done: "Mark done",
    reopen: "Reopen",
};

const optionsFor = (item: ActionItem): ItemOption[] =>
    item.action === "request_analysis" ? ["snooze", "auto_renew"] : ["snooze"];

/** Everything the dots menu offers: the platform's action or marking done first, then the ways out; reopen once done. */
const menuFor = (item: ActionItem): (PlatformAction | ItemOption)[] =>
    item.status === "done" ? ["reopen"] : [item.action ?? "done", ...optionsFor(item)];

const optionLabel = (item: ActionItem, option: PlatformAction | ItemOption): string =>
    option === "request_analysis" || option === "request_playbook"
        ? PLATFORM_ACTION_LABEL[option]
        : OPTION_LABEL[option];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * An item's due date as a tag: overdue in days, within the week with its
 * weekday, later as a date; done and canceled say so.
 */
const DueTag: FC<{ item: ActionItem }> = ({ item }) => {
    const days = daysUntil(item.due);
    const date = `${monthShort(item.due.slice(0, 7))} ${Number(item.due.slice(8, 10))}`;
    // Once ended, the date alone: the status column says the rest.
    if (item.status !== "open")
        return <span className={clsx(classes.tag, classes["tag--grey"])}>{date}</span>;
    if (days < 0)
        return (
            <span className={clsx(classes.tag, classes["tag--orange"])}>Overdue · {-days}d</span>
        );
    if (days === 0) return <span className={clsx(classes.tag, classes["tag--blue"])}>Today</span>;
    if (days <= 7) {
        const weekday = WEEKDAYS[new Date(item.due + "T00:00:00Z").getUTCDay()];
        return (
            <span className={clsx(classes.tag, classes["tag--blue"])}>{`${weekday}, ${date}`}</span>
        );
    }
    return <span className={clsx(classes.tag, classes["tag--grey"])}>{date}</span>;
};

/**
 * The stage's actions, folded under the strip: a header with the counts and
 * a chevron; open, one line per item with its checkbox, the platform's
 * button when it has one, and its due date.
 */
const ActionsCard: FC<{
    stage?: Stage;
    open?: boolean;
    /** The item the page was opened for, highlighted. */
    highlight?: string;
    /** The signed-in user's name: the owner of the items on them. */
    me: string;
    onToggle: (item: ActionItem, done: boolean) => void;
    onAction: (item: ActionItem, what: PlatformAction | ItemOption) => void;
    canAct: (item: ActionItem, option: PlatformAction | ItemOption) => string | undefined;
}> = ({ stage, open = false, highlight, me, onToggle, onAction, canAct }) => {
    const [expanded, setExpanded] = useState(open);
    const items = stage?.items ?? [];
    const openItems = items.filter((i) => i.status === "open");
    const overdue = openItems.filter(isOverdue).length;
    return (
        <div className={classes.actionList}>
            <button
                type="button"
                className={classes.actions__header}
                aria-expanded={expanded}
                onClick={() => setExpanded((e) => !e)}
            >
                <span className={classes.actions__title}>
                    Actions{stage ? ` · ${STAGE_TITLE[stage.kind]}` : ""}
                </span>
                <span className={classes.actions__summary}>
                    {items.length === 0
                        ? "Nothing to do on this stage"
                        : `${openItems.length} open${overdue ? ` · ${overdue} overdue` : ""}`}
                </span>
                <span className={classes.actions__chevron}>
                    <SvgIcon name={expanded ? "up" : "down"} size={12} aria-hidden="true" />
                </span>
            </button>
            {expanded &&
                items.map((item) => {
                    const ours = item.ownerKind === "infinityloop";
                    return (
                        <div
                            key={item.id}
                            className={clsx(
                                classes.action,
                                item.id === highlight && classes["action--highlight"]
                            )}
                        >
                            <span
                                className={clsx(
                                    classes.action__title,
                                    item.status !== "open" && classes["action__title--ended"]
                                )}
                            >
                                {item.title}
                            </span>
                            <span className={classes.action__owner}>
                                {ours ? (
                                    item.ownerLabel
                                ) : (
                                    <OwnerName
                                        name={
                                            item.ownerKind === "user"
                                                ? me
                                                : personName(item.ownerLabel)
                                        }
                                    />
                                )}
                            </span>
                            <span className={classes.action__status}>
                                <ActionItemStatus item={item} />
                            </span>
                            <span className={classes.action__due}>
                                <DueTag item={item} />
                            </span>
                            <span className={classes.action__menu}>
                                {!ours && item.status !== "canceled" && (
                                    <Dropdown
                                        trigger={["click"]}
                                        menu={{
                                            items: menuFor(item).map((o) => ({
                                                key: o,
                                                label: optionLabel(item, o),
                                                disabled: !!canAct(item, o),
                                            })),
                                            onClick: ({ key }) =>
                                                onAction(item, key as ItemOption | PlatformAction),
                                        }}
                                    >
                                        <span>
                                            <DataTableActionButton
                                                icon="more-vertical"
                                                label="Actions"
                                                color="grey"
                                            />
                                        </span>
                                    </Dropdown>
                                )}
                            </span>
                        </div>
                    );
                })}
        </div>
    );
};

export const UsageProjectView: FC = () => {
    const { projectId = "" } = useParams();
    const { me } = useUserState();
    const [params, setParams] = useSearchParams();
    const state = useUsageDemoState();
    const projects = useMemo(() => effectiveProjects(state), [state]);
    const project = projects.find((p) => p.id === projectId);

    if (!project) {
        return (
            <div className={classes.body}>
                <Empty description="No such project in the demo." />
            </div>
        );
    }

    const deal = dealById(project.dealId)!;
    const vendor = vendorOfDeal(deal);
    const stages = stagesOf(project);
    const reached = stages.filter((s) => s.reached);
    const latest = reached[reached.length - 1];
    const stage = reached.find((s) => s.kind === params.get("stage")) ?? latest;
    const revisions = stage?.revisions ?? [];
    const revision =
        revisions.find((r) => String(r.revision) === params.get("rev")) ??
        revisions[revisions.length - 1];

    const select = (kind: StageKind, rev?: number) =>
        setParams(rev ? { stage: kind, rev: String(rev) } : { stage: kind }, { replace: true });

    const decision = state.decisions[project.id];
    const lastUpdate = project.analyses
        .map((a) => a.publishedAt)
        .sort()
        .pop();
    // The notice deadline is the project's due date: in days here, the date on hover.
    const noticeDays = relativeDays(deal.term.noticeBy).replace("in ", "");
    const noticeOverdue = daysUntil(deal.term.noticeBy) < 0;
    const html = revision ? reportHtml(revision, project.id) : undefined;
    const endedCount = (s: Stage) => s.items.filter((i) => i.status !== "open").length;

    // ---- Stage actions -----------------------------------------------------
    const decideItem =
        stage?.kind === "snapshot" ? stage.items.find((i) => i.status === "open") : undefined;
    const onRenegotiate = () => {
        if (decideItem) setItemStatus(decideItem.id, "done");
        message.success(
            "Accepted. The usage analysis is requested; it is due within five working days."
        );
    };
    const onSnooze = () => {
        recordDecision(project.id, "snoozed");
        message.success(
            "Snoozed to 15 Nov. The project's items moved there; it left On me in the Pipeline."
        );
    };
    const onAutoRenew = () => {
        if (decideItem) setItemStatus(decideItem.id, "done");
        recordDecision(project.id, "auto_renew");
        message.success(
            "Decision recorded: let it auto-renew. The project is closed and left the Pipeline."
        );
    };
    const usageDone = stageComplete(project, "usage");
    const onRequestPlaybook = () => {
        requestPlaybook(project.id);
        message.success("Playbook requested. Round 1 is in progress; the stage moved to Playbook.");
        select("playbook");
    };

    // What the platform does when an item's button or one of its options is picked.
    const onAction = (item: ActionItem, what: PlatformAction | ItemOption) => {
        if (what === "request_analysis") onRenegotiate();
        if (what === "request_playbook") onRequestPlaybook();
        if (what === "snooze") {
            if (item.action === "request_analysis") onSnooze();
            else message.success(`Snoozed to 15 Nov: ${item.title}`);
        }
        if (what === "auto_renew") onAutoRenew();
        if (what === "done") {
            setItemStatus(item.id, "done");
            message.success(`Done: ${item.title}`);
        }
        if (what === "reopen") setItemStatus(item.id, "open");
    };
    const canAct = (_item: ActionItem, option: PlatformAction | ItemOption) =>
        option === "request_playbook" && !usageDone
            ? "Complete or cancel the other open moves first."
            : undefined;

    return (
        <div className={classes.page}>
            <div className={classes.stickyHeader}>
                <HeaderCard
                    vendor={vendor}
                    logoFor={deal.name}
                    name={project.name}
                    detail={
                        <>
                            {vendor.name} · <Link to={usageDealPath(deal.id)}>{deal.name}</Link>
                        </>
                    }
                >
                    <Field label="Status">
                        <UsageStageCell project={project} />
                    </Field>
                    <Field label="Utilization" width={160}>
                        <UsageMeasureCell deal={deal} stacked />
                    </Field>
                    <Field label="Savings" width={160}>
                        <SavingsProgress
                            target={project.estSavings * 100}
                            realized={0}
                            pending={(project.realizedSavings ?? 0) * 100}
                        />
                    </Field>
                    <Field label="TCV">{money(contractValue(deal))}</Field>
                    <Field label="Owner">
                        <OwnerName name={me.name} />
                    </Field>
                    <Field label="Due">
                        <Tooltip title={`Notice by ${longDate(deal.term.noticeBy)}`}>
                            <span className={clsx(noticeOverdue && classes.overdue)}>
                                {noticeDays}
                            </span>
                        </Tooltip>
                    </Field>
                    <Field label="Updated">
                        {lastUpdate ? relativeDays(lastUpdate).replace("in ", "") : "–"}
                    </Field>
                </HeaderCard>
            </div>

            <div className={classes.body}>
                {decision && (
                    <Alert
                        type="success"
                        showIcon
                        compact
                        message={
                            decision === "snoozed"
                                ? "Decision recorded: snoozed to 15 Nov. The project's items wait there in the Pipeline."
                                : "Decision recorded: let it auto-renew. The project is closed."
                        }
                    />
                )}

                <div className={classes.stageCard}>
                    <div className={classes.strip} role="tablist" aria-label="Stages">
                        {stages.map((s, index) => {
                            const selected = s === stage;
                            const done = endedCount(s);
                            const share = s.items.length ? done / s.items.length : 0;
                            const complete =
                                s.reached &&
                                s !== latest &&
                                (s.items.length === 0 || done === s.items.length);
                            const overdue = s.items.filter(isOverdue).length;
                            return (
                                <button
                                    key={s.kind}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    disabled={!s.reached}
                                    className={clsx(
                                        classes.step,
                                        selected && classes["step--selected"],
                                        !s.reached && classes["step--ahead"]
                                    )}
                                    onClick={() => select(s.kind)}
                                >
                                    <span className={classes.step__progress} aria-hidden="true">
                                        <span
                                            className={clsx(
                                                classes.step__fill,
                                                complete && classes["step__fill--complete"]
                                            )}
                                            style={{ width: `${Math.round(share * 100)}%` }}
                                        />
                                    </span>
                                    <span className={classes.step__title}>
                                        <span
                                            className={clsx(
                                                classes.step__icon,
                                                complete && classes["step__icon--complete"],
                                                !s.reached && classes["step__icon--ahead"]
                                            )}
                                        >
                                            {complete ? (
                                                <SvgIcon
                                                    name="check"
                                                    size={10}
                                                    aria-hidden="true"
                                                />
                                            ) : (
                                                index + 1
                                            )}
                                        </span>
                                        <span className={classes.step__name}>
                                            {STAGE_TITLE[s.kind]}
                                        </span>
                                    </span>
                                    <span className={classes.step__meta}>
                                        {!complete && (
                                            <span
                                                className={clsx(
                                                    classes.tag,
                                                    s === latest
                                                        ? classes["tag--blue"]
                                                        : classes["tag--grey"]
                                                )}
                                            >
                                                {s === latest
                                                    ? "Current"
                                                    : s.reached
                                                      ? "Done"
                                                      : "Upcoming"}
                                            </span>
                                        )}
                                        <span className={classes.step__count}>
                                            {done} of {s.items.length} actions done
                                        </span>
                                    </span>
                                    {overdue > 0 && (
                                        <span className={classes.step__meta}>
                                            <span
                                                className={clsx(
                                                    classes.tag,
                                                    classes["tag--orange"]
                                                )}
                                            >
                                                {overdue} overdue
                                            </span>
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <ActionsCard
                        key={stage?.kind}
                        stage={stage}
                        open={params.get("items") === "open"}
                        highlight={params.get("item") ?? undefined}
                        me={me.name}
                        onToggle={(item, done) => {
                            setItemStatus(item.id, done ? "done" : "open");
                            if (done) message.success(`Done: ${item.title}`);
                        }}
                        onAction={onAction}
                        canAct={canAct}
                    />
                </div>

                <section className={classes.report}>
                    {revisions.length > 1 && (
                        <div className={classes.reportHeader}>
                            {revisions.length > 1 && (
                                <Segmented
                                    size="small"
                                    value={String(revision?.revision)}
                                    onChange={(value) => stage && select(stage.kind, Number(value))}
                                    options={revisions.map((r) => ({
                                        label: `v${r.revision}, ${shortDate(r.publishedAt)}`,
                                        value: String(r.revision),
                                    }))}
                                />
                            )}
                        </div>
                    )}
                    {html ? (
                        <iframe
                            className={classes.frame}
                            srcDoc={html}
                            title={stage ? STAGE_TITLE[stage.kind] : "Analysis"}
                            sandbox="allow-same-origin allow-scripts"
                        />
                    ) : (
                        <div className={classes.pending}>
                            <Result
                                status="info"
                                title="Usage analysis in progress"
                                subTitle="Requested analyses are delivered within 24 hours. The Pipeline shows it under On InfinityLoop."
                            />
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};
